<ng-container *transloco="let t">
  <div
    [class.disabled]="disabled()"
    [class.error]="error()"
    class="file-upload-container"
  >
    @if (required() || maxNumberOfFiles() || maxFileSize()) {
      <p class="file-upload-restriction-text">
        @if (required() && maxNumberOfFiles() && maxNumberOfFiles() === 1) {
          <span>{{ t('fileupload_validation_text_same', { number: 1 }) }}</span>
        } @else if (required() && maxNumberOfFiles()) {
          <span>{{
            t('fileupload_validation_text_both', {
              min_number: 1,
              max_number: maxNumberOfFiles(),
            })
          }}</span>
        } @else if (required() && !maxNumberOfFiles()) {
          <span>{{ t('fileupload_validation_text_min', { number: 1 }) }}</span>
        } @else if (maxNumberOfFiles() && !required()) {
          <span>{{
            t('fileupload_validation_text_max', { number: maxNumberOfFiles() })
          }}</span>
        }
        @if (maxFileSize()) {
          <span>{{
            t('fileupload_validation_text_max_size', { size: maxFileSize() })
          }}</span>
        }
        @if (maxNumberOfPages()) {
          <span>{{
            t('fileupload_validation_text_maxpages', {
              pages: maxNumberOfPages(),
            })
          }}</span>
        }
      </p>
    }
    <input
      [attr.id]="id()"
      class="file-upload"
      type="file"
      [disabled]="disabled()"
      (change)="onFileSelected($event)"
      (click)="onWindowOpen()"
      [accept]="allowedFileTypes()"
      multiple
      [attr.aria-required]="required()"
      [attr.aria-invalid]="error()"
      [attr.aria-describedby]="error() ? 'required_text_' + id() : undefined"
    />
    <label
      [attr.for]="id()"
      class="upload-box"
      libDragAndDrop
      (fileDropped)="onFileDropped($event)"
      [isUploadDisabled]="disabled()"
      [attr.aria-disabled]="disabled()"
    >
      <span class="drag-box-content">
        <lib-icon name="upload-cloud" aria-hidden="true"></lib-icon>
        <span class="upload-text">{{ t('fileupload_upload') }}</span>
        <span class="drop-text">{{ t('fileupload_or_drop') }}</span>
      </span>
      <span class="drop-box-content">
        {{ t('fileupload_drop_here') }}
      </span>
    </label>
    @if (error()) {
      <div class="invalid-feedback">
        <lib-icon
          name="alert-circle"
          aria-hidden="true"
          size="x-small"
        ></lib-icon>
        <span [attr.id]="'required_text_' + id()">{{
          readonly() ? t('fileupload_required') : t('fileupload_error_required')
        }}</span>
      </div>
    }
    <div class="position-relative">
      <div aria-live="polite">
        @for (file of uploadedFiles(); track $index; let i = $index) {
          <div class="position-relative">
            @if (changedFileIds().includes(file.uploadId)) {
              <lib-dot-indicator type="file-upload" [isVisible]="true" />
            }
            <div aria-live="off">
              <div
                class="uploaded-file-container"
                [class.error]="file.status === UploadedFileStatus.Error"
                [class.tomanypages]="
                  file.status === UploadedFileStatus.ToManyPages
                "
              >
                <div class="d-flex">
                  @if (file.status !== UploadedFileStatus.Error) {
                    <span
                      class="uploaded-file-name"
                      [class.shrink]="
                        file.status === UploadedFileStatus.Loading ||
                        file.status === UploadedFileStatus.Uploaded
                      "
                      [class.success]="
                        file.status === UploadedFileStatus.Uploaded
                      "
                      triggers="click"
                      ><strong>{{ file.pdfFileName }}</strong> -
                      {{ file.originalFileName }}</span
                    >
                  }
                  @if (
                    file.status === UploadedFileStatus.Loading ||
                    file.status === UploadedFileStatus.Success
                  ) {
                    <div
                      class="spinner-border ms-auto d-flex align-self-center"
                      role="status"
                    ></div>
                  }
                  @if (file.status === UploadedFileStatus.Uploaded) {
                    <lib-icon
                      name="check-circle"
                      class="success-check"
                      aria-hidden="true"
                      size="x-small"
                    ></lib-icon>
                  }
                  @if (file.status === UploadedFileStatus.Error) {
                    <span
                      [innerHtml]="
                        t('fileupload_error', { name: file.originalFileName })
                      "
                    ></span>
                  }
                </div>
                @if (
                  file.status === UploadedFileStatus.Uploaded ||
                  file.status === UploadedFileStatus.ToManyPages
                ) {
                  <div class="file-button-container">
                    <button
                      type="button"
                      class="btn link-btn"
                      (click)="downloadFile(file)"
                    >
                      {{ t('fileupload_download_as_pdf') }}
                    </button>
                    {{ t('fileupload_or') }}
                    <button
                      type="button"
                      class="btn link-btn"
                      (click)="removeFile(file)"
                    >
                      {{ t('fileupload_delete_file') }}
                    </button>
                  </div>
                }
              </div>
              @if (file.status === UploadedFileStatus.Error) {
                <div class="invalid-feedback">
                  <lib-icon
                    name="alert-circle"
                    aria-hidden="true"
                    size="x-small"
                  ></lib-icon>
                  <span>{{
                    t(file.validationMessageKey, file.validationMessageParams)
                  }}</span>
                </div>
              }
              @if (file.status === UploadedFileStatus.ToManyPages) {
                <div class="error-message">
                  <p class="invalid-feedback">
                    <lib-icon
                      name="alert-circle"
                      aria-hidden="true"
                      size="x-small"
                    ></lib-icon>
                    <span>{{
                      t('fileupload_validation_maxpages', {
                        pages: file.validationMessageParams.pages,
                        maxPages: file.validationMessageParams.maxpages,
                      })
                    }}</span>
                  </p>
                </div>
              }
            </div>
            <div class="visually-hidden">
              @if (file.status === UploadedFileStatus.Loading) {
                <span>{{ t('fileupload_uploading') }}</span>
              }
              @if (file.status === UploadedFileStatus.Uploaded) {
                <span>{{ t('fileupload_uploading_success') }}</span>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</ng-container>
